import React from "react"
import "./Legal.css"

const Legal = () => {
  return (
    <div className="legal-container">
      <div className="legal-content">
        <h1>PinkcatAI – Terms & Conditions</h1>
        <p className="last-updated">Last updated: 26.12.2025</p>

        <section>
          <h2>1. Introduction & Scope</h2>
          <p>
            These Terms & Conditions ("Terms") govern the use of the PinkcatAI platform, website, and all related services (collectively, the "Service"), operated under the brand name PinkcatAI.
          </p>
          <p>PinkcatAI provides AI-powered tools that allow users to:</p>
          <ul>
            <li>Upload their own images,</li>
            <li>Generate AI-enhanced images based on those uploads,</li>
            <li>Use predefined AI models, styles, and templates provided by PinkcatAI.</li>
          </ul>
          <p>By accessing or using the Service, you agree to be bound by these Terms.</p>
        </section>

        <section>
          <h2>2. Eligibility</h2>
          <p>
            You must be at least 18 years old (or the legal age of majority in your jurisdiction) to use PinkcatAI.
          </p>
          <p>By using the Service, you confirm that you meet this requirement.</p>
        </section>

        <section>
          <h2>3. Description of the Service</h2>
          <p>PinkcatAI offers a digital, AI-based image generation service.</p>
          <p>The Service may include:</p>
          <ul>
            <li>Image enhancement,</li>
            <li>Image transformation,</li>
            <li>AI-generated variations,</li>
            <li>Use of prebuilt templates, styles, and models.</li>
          </ul>
          <p className="warning">
            ⚠️ PinkcatAI does not guarantee any specific outcome, quality, or suitability of generated images.
          </p>
        </section>

        <section>
          <h2>4. User Content & Uploaded Images</h2>
          <h3>4.1 Ownership & Rights</h3>
          <p>You retain ownership of any images you upload ("User Content").</p>
          <p>By uploading content, you confirm that:</p>
          <ul>
            <li>You own all necessary rights to the images, or</li>
            <li>You have obtained all required permissions from the rights holder,</li>
            <li>
              The content does not violate applicable laws or third-party rights (including copyright, trademark, or personality rights).
            </li>
          </ul>

          <h3>4.2 License to PinkcatAI</h3>
          <p>
            By uploading User Content, you grant PinkcatAI a non-exclusive, worldwide, royalty-free, temporary license to use, process, and modify the content solely for the purpose of providing the Service, including AI image generation, platform operation, and service improvement.
          </p>
        </section>

        <section>
          <h2>5. AI-Generated Images & Ownership</h2>
          <h3>Ownership of AI-Generated Images</h3>
          <p>
            Subject to your compliance with these Terms, all images generated through the PinkcatAI Service belong exclusively to you, the customer.
          </p>
          <p>
            PinkcatAI does not claim ownership of the AI-generated images created at your request. You are free to use, reproduce, modify, distribute, and exploit the generated images for personal or commercial purposes, provided that such use does not violate applicable laws or third-party rights.
          </p>
          <p>
            PinkcatAI retains ownership of the underlying AI models, templates, styles, software, algorithms, and platform technology used to generate the images. No rights to these underlying systems are transferred to you.
          </p>

          <h3>No Guarantee of Exclusivity</h3>
          <p>
            Due to the nature of artificial intelligence, PinkcatAI does not guarantee that AI-generated images will be unique or exclusive. Similar or identical images may be generated for other users.
          </p>
        </section>

        <section>
          <h2>6. Models, Templates & Platform Assets</h2>
          <p>
            All AI models, templates, styles, presets, and platform features ("Platform Assets") provided by PinkcatAI remain the exclusive intellectual property of PinkcatAI.
          </p>
          <p>You may not:</p>
          <ul>
            <li>Copy, extract, or reproduce templates or models,</li>
            <li>Reverse engineer the Service,</li>
            <li>Resell or redistribute Platform Assets outside the Service.</li>
          </ul>
        </section>

        <section>
          <h2>7. Prohibited Content & Conduct</h2>
          <p>You agree not to upload, generate, or use the Service for content that is:</p>
          <ul>
            <li>Illegal or unlawful,</li>
            <li>Infringing intellectual property or personal rights,</li>
            <li>Pornographic or sexually explicit,</li>
            <li>Violent, hateful, or discriminatory,</li>
            <li>Involving minors in any form,</li>
            <li>Misleading, fraudulent, or deceptive,</li>
            <li>Intended to impersonate real individuals without consent.</li>
          </ul>
          <p>
            PinkcatAI reserves the right to remove content and suspend or terminate accounts that violate these rules.
          </p>
        </section>

        <section>
          <h2>8. Subscriptions, Fees & Payments</h2>
          <p>PinkcatAI offers paid subscription and/or credit-based plans.</p>
          <ul>
            <li>Fees are charged in advance.</li>
            <li>Subscriptions renew automatically unless canceled before renewal.</li>
            <li>
              Due to immediate digital service delivery and computational costs, refunds are not provided, unless required by law.
            </li>
            <li>
              By purchasing, you expressly consent to immediate performance of the Service and waive any applicable withdrawal rights in accordance with § 356 Abs. 5 BGB, where legally permitted.
            </li>
          </ul>
        </section>

        <section>
          <h2>9. Account Responsibility</h2>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining the confidentiality of your account credentials,</li>
            <li>All activities conducted through your account.</li>
          </ul>
          <p>
            PinkcatAI is not liable for unauthorized access resulting from your failure to secure your account.
          </p>
        </section>

        <section>
          <h2>10. Disclaimer of Warranties</h2>
          <p>The Service is provided "as is" and "as available."</p>
          <p>
            PinkcatAI makes no warranties, express or implied, including but not limited to warranties of accuracy, reliability, merchantability, or fitness for a particular purpose.
          </p>
        </section>

        <section>
          <h2>11. Limitation of Liability</h2>
          <p>To the maximum extent permitted by German law:</p>
          <ul>
            <li>PinkcatAI shall not be liable for indirect, incidental, or consequential damages,</li>
            <li>
              Liability for slight negligence is excluded except in the case of essential contractual obligations (Kardinalpflichten),
            </li>
            <li>
              Total liability shall not exceed the amount paid by you for the Service within the last 12 months.
            </li>
          </ul>
        </section>

        <section>
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless PinkcatAI from any claims, damages, or expenses arising from:
          </p>
          <ul>
            <li>Your use of the Service,</li>
            <li>Your uploaded or generated content,</li>
            <li>Your violation of these Terms or applicable laws.</li>
          </ul>
        </section>

        <section>
          <h2>13. Termination</h2>
          <p>PinkcatAI may suspend or terminate your access if you violate these Terms.</p>
          <p>You may terminate your account at any time via your account settings.</p>
        </section>

        <section>
          <h2>14. Changes to These Terms</h2>
          <p>
            PinkcatAI may update these Terms from time to time. Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2>15. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the Federal Republic of Germany, excluding the UN Convention on Contracts for the International Sale of Goods (CISG).
          </p>
        </section>

        <section>
          <h2>16. Contact</h2>
          <p>For questions or legal inquiries:</p>
          <p>
            Email: <a href="mailto:supportpinkcatai@gmail.com">supportpinkcatai@gmail.com</a>
          </p>
        </section>
      </div>
    </div>
  )
}

export default Legal

